import {
  Dialog,
  DialogActions,
  IconButton,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import React from 'react'
import {
  Button,
  FormDataConsumer,
  sanitizeFetchType,
  SimpleForm,
  TextInput,
  Toolbar,
  useMutation,
  useNotify,
  useTranslate,
} from 'react-admin'
import { Close } from '@material-ui/icons'
import { useFormState } from 'react-final-form'
import ContentSave from '@material-ui/icons/Save'
import { CreateDialogProps } from '../common/customized-ra-components/CreateDialog'
import FilteredReferenceInput from '../common/FilteredReferenceInput'
import { ResourceName } from '../../core/ResourceName'
import { ticketsImportUpdateAsyncJobParams } from '../../core/ticket-imports/ticket-imports.provider'
import { FileInput } from './ticket-import-button/FileInput'

const useStyles = makeStyles({
  dialog: {
    '& > div > div': {
      width: '350px !important',
    },
  },
  bulkStateActions: {
    height: '16px',
    background: 'rgba(0,0,0,0.2)',
    userSelect: 'none',
  },
  bulkProgressTitle: {
    marginLeft: '10px',
    marginRight: 'auto',
  },
  bulkProgressCloseButton: {
    margin: 0,
  },
})

export interface TicketImportUpdateDialogProps extends CreateDialogProps {
  readonly importId?: number
}

const validateTicketImportUpdate = (
  values: ticketsImportUpdateAsyncJobParams,
) => {
  const errors: { [n: string]: string } = {}
  if (!values.name) {
    errors.name = 'ra.validation.required'
  }
  if (!values.importId) {
    errors.importId = 'ra.validation.required'
  }
  if (!values.csvFile) {
    errors.csvFile = 'ra.validation.required'
  }
  return errors
}

const CreateToolbar = ({ ...props }) => {
  const { handleClose, ...rest } = props
  const { invalid } = rest
  const { values } = useFormState()
  const [mutate] = useMutation()
  const notify = useNotify()

  const handleSave = async () => {
    await mutate(
      {
        type: sanitizeFetchType('ticketsImportUpdateAsyncJob'),
        resource: ResourceName.TICKET_IMPORTS,
        payload: {
          ...values,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
    handleClose()
  }

  return (
    <Toolbar {...rest}>
      <Button
        disabled={invalid}
        onClick={handleSave}
        label="ra.action.save"
        startIcon={<ContentSave />}
        variant="contained"
      />
    </Toolbar>
  )
}

export const TicketImportUpdateDialog = (
  props: TicketImportUpdateDialogProps,
) => {
  const { importId, ...rest } = props
  const { handleClose } = rest
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyles()
  const translate = useTranslate()

  return (
    <Dialog
      {...rest}
      className={classes.dialog}
      title={translate(
        'resources.ticket-imports.actions.importTicketsUpdateAsyncJob',
      )}
    >
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
        id="draggable-dialog-title"
      >
        <Typography className={classes.bulkProgressTitle}>
          {translate(
            'resources.ticket-imports.actions.importTicketsUpdateAsyncJob',
          )}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <SimpleForm
        initialValues={{
          importId,
        }}
        toolbar={<CreateToolbar handleClose={handleClose} />}
        validate={validateTicketImportUpdate}
      >
        <TextInput
          source="name"
          fullWidth
          label={translate('resources.ticket-imports.fields.name')}
        />
        <FilteredReferenceInput
          label={translate('resources.ticket-imports.fields.baseImportId')}
          source="importId"
          defaultValue={null}
          reference={ResourceName.TICKET_IMPORTS}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="name"
          selectWithPaginationInputProps={{
            optionText: 'name',
            showFilter: true,
          }}
          fullWidth
        />
        <FormDataConsumer fullWidth>
          {({ formData, ...formRest }) => (
            <FileInput
              {...formRest}
              formData={formData}
              fullWidth
              source="csvFile"
              label={translate('resources.ticket-imports.fields.csvFile')}
            />
          )}
        </FormDataConsumer>
      </SimpleForm>
    </Dialog>
  )
}
